import type { WeightSampleLog } from '../../types/production'

// Re-export the shared D-3 pond → lot resolution so this module is the
// single import surface for WeightSampleCapture.tsx, mirroring
// mortalityCaptureValidation.ts / feedCaptureValidation.ts.
export { resolvePondLots, type ResolvedPondLots } from './pondLotResolution'

// WeightSample v0 PRD §3 / §5 / Stage 1 Thread A: `weightGrams` and
// `sampleCount` both have authoritative server-enforced bounds
// (`WeightSampleLogService.ts` — `weightGrams` `0 < x ≤ 10000` g,
// `sampleCount` integer `≥ 1`). Unlike Feed's `feedKg` / Mortality's
// `mortalityCount` (no server maximum → invent a soft client threshold),
// WeightSample follows the WaterLog pattern: the client mirrors the exact
// server bounds and blocks Confirm on an out-of-range value rather than
// offering "save anyway" — a value the server would reject with a 400
// must not be queued as if it saved. No standing-biomass figure, daily
// mean, or lot state of any kind is read to reach this validation
// (Charter O-1, O-2, O-3).

// Mirror of the server bound in `WeightSampleLogService.ts` weightGramsSchema.
export const WEIGHT_GRAMS_MIN_EXCLUSIVE = 0
export const WEIGHT_GRAMS_MAX = 10000
// Mirror of the server bound in `WeightSampleLogService.ts` sampleCountSchema.
export const SAMPLE_COUNT_MIN = 1

export type WeightGramsValidation =
  | { ok: true; value: number }
  | { ok: false; reason: 'invalid' | 'out-of-range' }

// Mirrors the server's numeric coercion: strip grouping commas, then parse.
const parseNumericLike = (rawValue: string): number => Number(rawValue.replaceAll(',', '').trim())

export const validateWeightGramsValue = (rawValue: string): WeightGramsValidation => {
  const trimmed = rawValue.trim()
  if (trimmed.length === 0) return { ok: false, reason: 'invalid' }

  const value = parseNumericLike(trimmed)
  if (!Number.isFinite(value)) return { ok: false, reason: 'invalid' }
  if (value <= WEIGHT_GRAMS_MIN_EXCLUSIVE || value > WEIGHT_GRAMS_MAX) return { ok: false, reason: 'out-of-range' }

  return { ok: true, value }
}

// PRD `weightsample-individual-length-v1.md` §5: lengthMm mirrors
// weightGrams's shape exactly — bounded, optional, no lot-state read.
export const LENGTH_MM_MIN_EXCLUSIVE = 0
export const LENGTH_MM_MAX = 2000

export type LengthMmValidation =
  | { ok: true; value: number | undefined }
  | { ok: false; reason: 'invalid' | 'out-of-range' }

export const validateLengthMmValue = (rawValue: string): LengthMmValidation => {
  const trimmed = rawValue.trim()
  if (trimmed.length === 0) return { ok: true, value: undefined }

  const value = parseNumericLike(trimmed)
  if (!Number.isFinite(value)) return { ok: false, reason: 'invalid' }
  if (value <= LENGTH_MM_MIN_EXCLUSIVE || value > LENGTH_MM_MAX) return { ok: false, reason: 'out-of-range' }

  return { ok: true, value }
}

export type SampleCountValidation =
  | { ok: true; value: number | undefined }
  | { ok: false; reason: 'invalid' | 'out-of-range' }

// PRD §3 / §11 AC #10 / Stage 2 finding #5: `sampleCount` is OPTIONAL in
// this flow (the server schema types it `.optional()`). An empty field is
// a valid submission that produces a `WeightSampleLog` with no
// `sampleCount`. Only a present-but-malformed value is rejected.
export const validateSampleCountValue = (rawValue: string): SampleCountValidation => {
  const trimmed = rawValue.trim()
  if (trimmed.length === 0) return { ok: true, value: undefined }

  const value = parseNumericLike(trimmed)
  if (!Number.isFinite(value) || !Number.isInteger(value)) return { ok: false, reason: 'invalid' }
  if (value < SAMPLE_COUNT_MIN) return { ok: false, reason: 'out-of-range' }

  return { ok: true, value }
}

// WeightSample v0 PRD §3 / §10 P0 "Bloqueo de duplicados razonable": warn,
// never hard-block, on a same-lot entry within a short window. Same
// 15-minute default and warn-don't-block shape as WaterLog, Feed, and
// Mortality, scoped by `lotId` (WeightSample is lot-scoped, D-1).
export const DUPLICATE_WARNING_WINDOW_MS = 15 * 60 * 1000

// PRD `weightsample-individual-length-v1.md` §3 item 5 (Stage 2 finding 1):
// a candidate carrying `lengthMm` comes from the rapid individual-entry
// flow, where many distinct fish for the same lot legitimately land inside
// the same 15-minute window — comparing lotId+time alone would warn on
// every reading after the first. For that candidate, only warn when an
// existing log matches on lotId, time window, AND both weightGrams and
// lengthMm exactly — a genuine accidental double-tap, not two different
// fish. A legacy candidate (no lengthMm) keeps the original lotId+time-only
// check, unchanged, so `weightsample-v0.md` AC #16 still holds.
export const findLikelyDuplicateWeightSampleLog = (args: {
  lotId: string
  recordedAtIso: string
  weightGrams?: number
  lengthMm?: number
  existingLogs: WeightSampleLog[]
  // Office Plans v0 live check: a "one fish at a time" round without length
  // (planned weight-only sampling) must not warn on every fish — only an
  // exact weight (+ length) repeat counts, as with length-bearing readings.
  individual?: boolean
}): WeightSampleLog | null => {
  const candidateMs = Date.parse(args.recordedAtIso)
  if (!Number.isFinite(candidateMs)) return null

  for (const log of args.existingLogs) {
    if (log.lotId !== args.lotId) continue

    const existingMs = Date.parse(log.recordedAt)
    if (!Number.isFinite(existingMs)) continue
    if (Math.abs(existingMs - candidateMs) > DUPLICATE_WARNING_WINDOW_MS) continue

    if (args.lengthMm === undefined && !args.individual) {
      return log
    }

    const existingLengthMm = log.lengthMm !== undefined ? Number(log.lengthMm) : undefined
    const existingWeightGrams = Number(log.weightGrams)
    if (existingLengthMm === args.lengthMm && existingWeightGrams === args.weightGrams) {
      return log
    }
  }

  return null
}

export type WeightSampleCaptureCreatePayload = {
  farmId: string
  productionUnitId: string
  lotId: string
  lot?: string
  weightGrams: number
  lengthMm?: number
  sampleCount?: number
  recordedAt: string
  capturedByActorId: string | undefined
}

/**
 * Builds the create payload for a single Crew Mode WeightSample capture.
 * Pure and independently testable — mirrors
 * `buildMortalityCaptureCreatePayload`'s discipline:
 * - always stamps `capturedByActorId` from the active operator session
 *   (Charter D-4) rather than leaving it for the server's device-fallback;
 * - carries only facts — one sampled weight and an optional sample count.
 *   No daily mean, population-weighted rollup, standing biomass, biomass
 *   baseline, or growth rate appears here (Charter O-1, O-2, O-3);
 * - never sets `productionRowId` or `metadata.productionRowId` (Stage 1
 *   Thread B / Stage 2 finding #3) — the sample still rolls into the admin
 *   daily rollup by `(lot, day)`; associating it to a production row and
 *   deciding whether it resets standing biomass are supervisor concerns.
 * - stamps `lot` (the lot's plain-text key) alongside the authoritative
 *   `lotId` — the admin overlay bridge (weightSampleLogBridge.ts) keys its
 *   `(lot, day)` rollup on this string field, not `lotId`. Without it the
 *   sample is stored correctly but never surfaces in Biomass/Database.
 */
export const buildWeightSampleCaptureCreatePayload = (args: {
  farmId: string
  productionUnitId: string
  lotId: string
  lot?: string
  weightGrams: number
  lengthMm?: number
  sampleCount: number | undefined
  recordedAtIso: string
  capturedByActorId: string | undefined
}): WeightSampleCaptureCreatePayload => {
  const payload: WeightSampleCaptureCreatePayload = {
    farmId: args.farmId,
    productionUnitId: args.productionUnitId,
    lotId: args.lotId,
    weightGrams: args.weightGrams,
    recordedAt: args.recordedAtIso,
    capturedByActorId: args.capturedByActorId
  }

  if (args.lot) {
    payload.lot = args.lot
  }

  if (args.lengthMm !== undefined) {
    payload.lengthMm = args.lengthMm
  }

  if (args.sampleCount !== undefined) {
    payload.sampleCount = args.sampleCount
  }

  return payload
}
